import { VirtualDocument } from "../../common/resources/virtual-document";
import { Product, ProductStandardHistory } from "../../order/resources/product";
import { Customer, CustomerContact } from "../../customer/resources/customer";
import { User } from "../../common/resources/user";
import { Address } from "../../common/resources/address";
import { RMADispositionLot, RMATicket } from "../../rma/resources/rma-ticket";
import { CustomerPurchaseOrder } from "./customer-po";
import { WorkOrder } from "../../planning/resources/work-order";
import { Station } from "./station";
import { Company } from "../../common/resources/company";
import { TaskList } from "../../common/resources/estimatingtask";
import { QualityTicket } from "@cots/quality/resources/quality-ticket";
import { assertUnreachable } from "util/assertUnreachable";

export type EstimateReviewAnswersMap = { [productId: string]: { [categoryId: string]: { [questionId: string]: string } } };

export interface QuoteLineItem {
  lineItemNumber?: string;
  quoteLineItemId: string;
  quoteId: string;
  quantity: number
  leadTimeDays: number
  unitPrice: number
  extPrice: number
  productId: string
  product: Product
  stationId: string
  station: Station
}

export interface Order {
  orderSegmentId: string;
  quoteNotes: string;
  privateNotes: string;
  publicNotes: string;
  estimateNotes: string;
  statusNote: string;
  createdDate?: Date;
  receivedDate?: Date;
  requiredDate?: Date;
  estimatedEstimateCompletionDate?: Date;
  issuedDate?: Date;
  legacyId: string;
  customerId: string;
  status: number;
  updatedDate?: Date;
  modifiedBy?: string;
  customerContactId?: string;
  orderNumber: string;
  customerRfqnumber: string;
  discriminator: string;
  poNumber: string;
  projectDescription: string;
  assignedTo: string;
  assignedUser: User;
  companyId: string;
  company: Company;

  shipToAddressId?: string;
  shipToAddress: Address;

  rmaTicketId?: string;
  rmaTicket: RMATicket;

  rmaDispositionLotId?: string;
  rmaDispositionLot: RMADispositionLot;

  lineItems: QuoteLineItem[];

  quoteNumber: string;
  estimateNumber: string;

  shippingAssignments: {
    [key: string]: {
      quantity: number,
      shipDate: Date
    }[]
  }

  quotePurchaseOrders: CustomerPurchaseOrder[];

  orderPurchaseOrderId?: string;
  orderPurchaseOrder?: CustomerPurchaseOrder;

  qualityTickets?: QualityTicket[];

  customer?: Customer;
  customerContact?: CustomerContact;
  products?: OrderProduct[];
  documents?: OrderDocument[]

  rfqReviewer?: User;
  dateRfqReviewed?: Date;


  salesProcessId?: string;

  creatorId?: string;
  creator: User;
  
  salesPersonId?: string;
  salesPerson?: User;

  estimatorId?: string;
  estimator?: User;

  estimatePromiseDateNotes: string;

  estimateReviewAnswers: EstimateReviewAnswersMap;

  contractRating: ContractRating;
}

export enum ContractRating {
  UNRATED = 0,
  EXPEDITE = 10,
  DO = 20,
  DX = 30,
}

export function getContractRatingValues(): ContractRating[] {
  return Object.values(ContractRating).filter((item) => {
      return !isNaN(Number(item));
  }).filter(i => i !== ContractRating.UNRATED).sort((a: number, b: number) => b - a) as ContractRating[];
}

export function getContractRatingChipClass(rating: ContractRating) {
  switch (rating) {
    case ContractRating.UNRATED: return "";
    case ContractRating.EXPEDITE: return "bg-info text-white";
    case ContractRating.DO: return "bg-warning text-dark";
    case ContractRating.DX: return "bg-danger text-white";
    default:
      assertUnreachable(rating);
  }
}

export function getContractRatingText(rating: ContractRating) {
  switch (rating) {
    case ContractRating.UNRATED: return "Unrated";
    case ContractRating.EXPEDITE: return "Expedite";
    case ContractRating.DO: return "DO";
    case ContractRating.DX: return "DX";
    default:
      assertUnreachable(rating);
  }
}

export class OrderStatus {
  static IN_PROCESS: number = 0;
  static AWAITING_REVIEW: number = 1;
  static HISTORY: number = 2;
  static REJECTED: number = 3;
  static APPROVED: number = 4;
  static TENT_APPROVED: number = 5;
  static AWAIT_ESTIMATE: number = 6;
  static AWAIT_PRICING: number = 7;
  static AWAIT_PO: number = 8;
  static ON_FLOOR: number = 9;
  static SHIPPING: number = 10;
  static FULFILLED: number = 11;
  static EXPEDITED: number = 12;
  static QUEUED: number = 13;
  static ON_HOLD: number = 14;
  static UNASSIGNED: number = 15;
  static QC_HOLD: number = 16;
  static EXPIRED: number = 17;
  static PRE_PLAN: number = 18;
  static CLOSED: number = 19;
  static AWAIT_ISSUANCE: number = 20;
  static PREPLANNING: number = 21;
  static AWAIT_PROGRAMMING: number = 22;
  static PREREQUISITE_MISSING = 23;
  static PRIOR_STEP_PREREQUISITE_MISSING = 24;
  static PLANNED = 25;
  static ON_ORDER = 99;
  static PO_AWARDED = 100;
  static AWAIT_QC = 201;

  static getStatusColorClass(status: number): string {
    switch (status) {
      case OrderStatus.IN_PROCESS: return 'badge-warning';
      case OrderStatus.AWAITING_REVIEW: return 'badge-info';
      case OrderStatus.HISTORY: return 'badge-secondary';
      case OrderStatus.REJECTED: return 'badge-danger';
      case OrderStatus.APPROVED: return 'badge-success';
      case OrderStatus.TENT_APPROVED: return 'badge-success';
      case OrderStatus.AWAIT_PRICING: return 'badge-info';
      case OrderStatus.AWAIT_ESTIMATE: return 'badge-secondary';
      case OrderStatus.AWAIT_PO: return 'badge-info';
      case OrderStatus.ON_FLOOR: return 'badge-success';
      case OrderStatus.SHIPPING: return 'badge-info';
      case OrderStatus.FULFILLED: return 'badge-success';
      case OrderStatus.EXPEDITED: return 'badge-danger';
      case OrderStatus.QUEUED: return 'badge-secondary';
      case OrderStatus.ON_HOLD: return 'badge-secondary';
      case OrderStatus.UNASSIGNED: return 'badge-danger';
      case OrderStatus.QC_HOLD: return 'badge-secondary';
      case OrderStatus.EXPIRED: return 'badge-danger';
      case OrderStatus.CLOSED: return 'badge-secondary';
      case OrderStatus.AWAIT_ISSUANCE: return 'badge-warning';
      case OrderStatus.PREPLANNING: return 'badge-warning';
      case OrderStatus.PLANNED: return 'badge-success';
      case OrderStatus.PO_AWARDED: return 'badge-success';
      case OrderStatus.AWAIT_QC: return 'badge-info';
      default: return null;
    }
  }

  static getStatusColorClassChip(status: number): string {
    switch (status) {
      case OrderStatus.IN_PROCESS: return 'bg-warning text-dark';
      case OrderStatus.AWAITING_REVIEW: return 'bg-info text-white';
      case OrderStatus.HISTORY: return 'bg-secondary text-dark';
      case OrderStatus.REJECTED: return 'bg-danger text-white';
      case OrderStatus.APPROVED: return 'bg-success text-white';
      case OrderStatus.TENT_APPROVED: return 'bg-success text-white';
      case OrderStatus.AWAIT_PRICING: return 'bg-info text-white';
      case OrderStatus.AWAIT_ESTIMATE: return 'bg-secondary text-dark';
      case OrderStatus.AWAIT_PO: return 'bg-info text-white';
      case OrderStatus.ON_FLOOR: return 'bg-success text-white';
      case OrderStatus.SHIPPING: return 'bg-info text-white';
      case OrderStatus.FULFILLED: return 'bg-success text-white';
      case OrderStatus.EXPEDITED: return 'bg-danger text-white';
      case OrderStatus.QUEUED: return 'bg-secondary text-dark';
      case OrderStatus.ON_HOLD: return 'bg-secondary text-dark';
      case OrderStatus.UNASSIGNED: return 'bg-danger text-white';
      case OrderStatus.QC_HOLD: return 'bg-secondary text-dark';
      case OrderStatus.EXPIRED: return 'bg-danger text-dark';
      case OrderStatus.CLOSED: return 'bg-secondary text-dark';
      case OrderStatus.AWAIT_ISSUANCE: return 'bg-warning text-dark';
      case OrderStatus.PREPLANNING: return 'bg-warning text-dark';
      case OrderStatus.PO_AWARDED: return 'bg-success text-white';
      case OrderStatus.ON_ORDER: return 'bg-info text-white';
      case OrderStatus.AWAIT_QC: return 'bg-info text-white';
      default: return null;
    }
  }

  static getStatusColorCode(status: number): string {
    switch (status) {
      case OrderStatus.IN_PROCESS: return '#28B463';
      case OrderStatus.AWAITING_REVIEW: return '#2980B9';
      case OrderStatus.HISTORY: return '#616A6B';
      case OrderStatus.REJECTED: return '#E74C3C';
      case OrderStatus.APPROVED: return '#28B463';
      case OrderStatus.TENT_APPROVED: return '#28B463';
      case OrderStatus.AWAIT_PRICING: return '#2980B9';
      case OrderStatus.AWAIT_ESTIMATE: return '#616A6B';
      case OrderStatus.AWAIT_PO: return 'badge-info';
      case OrderStatus.ON_FLOOR: return '#28B463';
      case OrderStatus.SHIPPING: return '#28B463';
      case OrderStatus.FULFILLED: return '#28B463';
      case OrderStatus.EXPEDITED: return '#E74C3C';
      case OrderStatus.QUEUED: return '#616A6B';
      case OrderStatus.ON_HOLD: return '#616A6B';
      case OrderStatus.UNASSIGNED: return '#E74C3C';
      case OrderStatus.QC_HOLD: return '#616A6B';
      case OrderStatus.EXPIRED: return '#E74C3C';
      case OrderStatus.CLOSED: return '#616A6B';
      case OrderStatus.AWAIT_ISSUANCE: return '#28B463';
      case OrderStatus.PREPLANNING: return '#28B463';
      case OrderStatus.PO_AWARDED: return '#28B463';
      default: return null;
    }
  }

  static getStatusText(status: number): string {
    switch (status) {
      case OrderStatus.IN_PROCESS: return 'In Process';
      case OrderStatus.AWAITING_REVIEW: return 'Awaiting Review';
      case OrderStatus.HISTORY: return 'History';
      case OrderStatus.REJECTED: return 'Rejected';
      case OrderStatus.APPROVED: return 'Approved';
      case OrderStatus.TENT_APPROVED: return 'Tentative Approval';
      case OrderStatus.AWAIT_PRICING: return 'Awaiting Pricing';
      case OrderStatus.AWAIT_ESTIMATE: return 'Awaiting Estimate';
      case OrderStatus.AWAIT_PO: return 'Ready for POs';
      case OrderStatus.ON_FLOOR: return 'On Floor';
      case OrderStatus.SHIPPING: return 'Shipping';
      case OrderStatus.FULFILLED: return 'Fulfilled';
      case OrderStatus.EXPEDITED: return 'Expedited';
      case OrderStatus.QUEUED: return 'Queued';
      case OrderStatus.ON_HOLD: return 'On Hold';
      case OrderStatus.UNASSIGNED: return 'Unassigned';
      case OrderStatus.QC_HOLD: return 'QC Hold';
      case OrderStatus.EXPIRED: return 'Expired';
      case OrderStatus.CLOSED: return 'Closed';
      case OrderStatus.AWAIT_ISSUANCE: return 'Not Issued';
      case OrderStatus.PREPLANNING: return 'Preplanning';
      case OrderStatus.PLANNED: return 'Planned';
      case OrderStatus.ON_ORDER: return 'On Order';
      case OrderStatus.PO_AWARDED: return 'PO Awarded';
      case OrderStatus.AWAIT_QC: return 'Await QC';
      default: return null;
    }
  }

  static isEditable(status: number): boolean {
    if (status == null) return true; //New one...

    switch (status) {
      case OrderStatus.UNASSIGNED:
      case OrderStatus.IN_PROCESS:
      case OrderStatus.TENT_APPROVED:
      case OrderStatus.AWAITING_REVIEW:
      case OrderStatus.AWAIT_PRICING:
      case OrderStatus.REJECTED:
        return true;
      default: return false;
    }
  }
}

export class EstimatingTaskStatus {
      static NOT_STARTED: number = 0;
      static IN_PROGRESS: number = 1;
      static DONE: number = 2;
      static NOT_APPLICABLE: number = 3;
      static NEEDS_QUOTE: number = 4;

  public static getStatusColorClass(status: EstimatingTaskStatus) {
    switch (status) {
      case (EstimatingTaskStatus.NOT_STARTED): return 'progress--not_started';
      case (EstimatingTaskStatus.IN_PROGRESS): return 'progress--in_progress';
      case (EstimatingTaskStatus.DONE): return 'progress--done';
      case (EstimatingTaskStatus.NOT_APPLICABLE): return 'progress--not_applicable';
      case (EstimatingTaskStatus.NEEDS_QUOTE): return 'progress--needs_quote';
    }
  }

  public static getStatusIcon(status: number) {
    switch (status) {
      case (EstimatingTaskStatus.NOT_STARTED): return 'alert-circle';
      case (EstimatingTaskStatus.IN_PROGRESS): return 'dots-horizontal';
      case (EstimatingTaskStatus.DONE): return 'check';
      case (EstimatingTaskStatus.NOT_APPLICABLE): return 'border-none-variant';
      case (EstimatingTaskStatus.NEEDS_QUOTE): return 'clipboard-clock';
    }
  }

  public static getStatusText(status: number) {
    switch (status) {
      case (EstimatingTaskStatus.NOT_STARTED): return 'Not Started';
      case (EstimatingTaskStatus.IN_PROGRESS): return 'In Progress';
      case (EstimatingTaskStatus.DONE): return 'Done';
      case (EstimatingTaskStatus.NOT_APPLICABLE): return 'Not Applicable';
      case (EstimatingTaskStatus.NEEDS_QUOTE): return 'Needs Quote';
    }
  }

  public static countsAsNotStarted(status: EstimatingTaskStatus): boolean {
    switch (status) {
      case EstimatingTaskStatus.NOT_STARTED:
        return true;
      case EstimatingTaskStatus.IN_PROGRESS:
      case EstimatingTaskStatus.NEEDS_QUOTE:
      case EstimatingTaskStatus.NOT_APPLICABLE:
      case EstimatingTaskStatus.DONE:
        return false;
    }
  }

  public static countsAsDone(status: EstimatingTaskStatus) {
    switch (status) {
      case EstimatingTaskStatus.NOT_APPLICABLE:
      case EstimatingTaskStatus.DONE:
        return true;
      case EstimatingTaskStatus.NOT_STARTED:
      case EstimatingTaskStatus.IN_PROGRESS:
      case EstimatingTaskStatus.NEEDS_QUOTE:
        return false;
    }
  }

  public static countsAsInProgress(status: EstimatingTaskStatus) {
    switch (status) {
      case EstimatingTaskStatus.IN_PROGRESS:
      case EstimatingTaskStatus.NEEDS_QUOTE:
        return true;
      case EstimatingTaskStatus.NOT_APPLICABLE:
      case EstimatingTaskStatus.DONE:
      case EstimatingTaskStatus.NOT_STARTED:
        return false;
    }
    if (status === EstimatingTaskStatus.IN_PROGRESS) return true;
    if (status === EstimatingTaskStatus.NEEDS_QUOTE) return true;
    return false;
  }
}

export interface OrderDocument {
  orderSegmentOrderSegmentId: string;
  documentDocumentId: string;

  document?: VirtualDocument
}

export enum OrderSegmentProductType {
  NEW = 0,
  CLONED = 1,
  CLONED_NEWREV = 2
}

export enum OrderSegmentProductReviewStatus {
  OnHold = -1,
  NotReviewed = 0,
  Incomplete = 1,
  Complete = 2,
  Corrected = 3,
  NoQuote = -100,
}

export function getRFQReviewStatusTooltip(status: OrderSegmentProductReviewStatus, currentlyReviewing: boolean = true) {
  switch (status) {
    case OrderSegmentProductReviewStatus.OnHold:
      return "On Hold, Click to Edit";
    case OrderSegmentProductReviewStatus.NotReviewed:
      if (currentlyReviewing) return "Review Product";
      else return "Not Reviewed";
    case OrderSegmentProductReviewStatus.Incomplete:
    case OrderSegmentProductReviewStatus.Complete:
    case OrderSegmentProductReviewStatus.Corrected:
      return "View Review Notes";
    case OrderSegmentProductReviewStatus.NoQuote:
      return "No Quote, Click to Edit";
    default:
      assertUnreachable(status);
  }
}

export function getRFQReviewStatusIcon(status: OrderSegmentProductReviewStatus, currentlyReviewing: boolean = true) {
  switch (status) {
    case OrderSegmentProductReviewStatus.OnHold:
      return "hand-front-right";
    case OrderSegmentProductReviewStatus.NotReviewed:
      if (currentlyReviewing) return "receipt-text-edit";
      else return "help-box-outline";
    case OrderSegmentProductReviewStatus.Incomplete:
      return "alert-box";
    case OrderSegmentProductReviewStatus.Complete:
    case OrderSegmentProductReviewStatus.Corrected:
      return "checkbox-marked";
    case OrderSegmentProductReviewStatus.NoQuote:
      return "receipt-text-remove";
    default:
      assertUnreachable(status);
  }
}

export function getRFQReviewStatusColor(status: OrderSegmentProductReviewStatus) {
  switch (status) {
    case OrderSegmentProductReviewStatus.OnHold:
      return "text-primary";
    case OrderSegmentProductReviewStatus.NotReviewed:
      return "text-muted";
    case OrderSegmentProductReviewStatus.Incomplete:
      return "text-warning";
    case OrderSegmentProductReviewStatus.Complete:
    case OrderSegmentProductReviewStatus.Corrected:
      return "text-success";
    case OrderSegmentProductReviewStatus.NoQuote:
      return "text-muted";
    default:
      assertUnreachable(status);
  }
}

export function getRFQReviewStatusChipClass(status: OrderSegmentProductReviewStatus) {
  switch (status) {
    case OrderSegmentProductReviewStatus.OnHold:
      return "text-primary";
    case OrderSegmentProductReviewStatus.NotReviewed:
      return "text-muted";
    case OrderSegmentProductReviewStatus.Incomplete:
    case OrderSegmentProductReviewStatus.Corrected:
      return "bg-warning text-dark";
    case OrderSegmentProductReviewStatus.Complete:
      return "text-success";
    case OrderSegmentProductReviewStatus.NoQuote:
      return "text-muted";
    default:
      assertUnreachable(status);
  }
}

export function getRFQReviewStatusName(status: OrderSegmentProductReviewStatus) {
  switch (status) {
    case OrderSegmentProductReviewStatus.OnHold:
      return "On Hold";
    case OrderSegmentProductReviewStatus.NotReviewed:
      return "Not Reviewed";
    case OrderSegmentProductReviewStatus.Incomplete:
    case OrderSegmentProductReviewStatus.Corrected:
      return "Incomplete";
    case OrderSegmentProductReviewStatus.Complete:
      return "Complete";
    case OrderSegmentProductReviewStatus.NoQuote:
      return "No Quote";
    default:
      assertUnreachable(status);
  }
}

export function getRFQReviewStatusChipIcon(status: OrderSegmentProductReviewStatus) {
  switch (status) {

    case OrderSegmentProductReviewStatus.Incomplete:
    case OrderSegmentProductReviewStatus.Corrected:
      return "exclamation";
    case OrderSegmentProductReviewStatus.Complete:
      return "check";
    case OrderSegmentProductReviewStatus.NoQuote:
      return "close";    case OrderSegmentProductReviewStatus.OnHold:
    case OrderSegmentProductReviewStatus.NotReviewed:
    case OrderSegmentProductReviewStatus.NotReviewed:
      return null;
    default:
      assertUnreachable(status);
  }
}


export interface OrderProduct {
  orderSegmentOrderSegmentId: string;
  productProductId: string;

  product?: Product;
  sort?: number;
  type?: OrderSegmentProductType;
  history?: ProductStandardHistory;

  reviewStatus: OrderSegmentProductReviewStatus;
  reviewStatusNote: string;
}
